
import React from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ChevronRight, Info, Shield, CreditCard, Heart, MessageCircle, User, Lock } from 'lucide-react';
import { useLanguage } from '@/context/language-context';

const HowItWorks = () => {
  const { t, currentLanguage } = useLanguage();
  
  return (
    <Layout>
      <div className="py-12 px-4 bg-gradient-to-b from-purple-50 to-pink-50 dark:from-background dark:to-background">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-600">
              {t('how_it_works.title')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('how_it_works.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-card rounded-xl p-8 shadow-md">
              <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900/20 rounded-full flex items-center justify-center mb-6">
                <User className="text-pink-500" size={24} />
              </div>
              <h2 className="text-2xl font-semibold mb-4">{t('how_it_works.for_clients')}</h2>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <ChevronRight className="text-pink-500 shrink-0 mt-1" size={18} />
                  <div>
                    <span className="font-medium">{t('how_it_works.browse_profiles')}</span> - {t('how_it_works.browse_profiles_desc')}
                  </div>
                </li>
                <li className="flex gap-3">
                  <ChevronRight className="text-pink-500 shrink-0 mt-1" size={18} />
                  <div>
                    <span className="font-medium">{t('how_it_works.subscribe')}</span> - {t('how_it_works.subscribe_desc')}
                  </div>
                </li>
                <li className="flex gap-3">
                  <ChevronRight className="text-pink-500 shrink-0 mt-1" size={18} />
                  <div>
                    <span className="font-medium">{t('how_it_works.connect')}</span> - {t('how_it_works.connect_desc')}
                  </div>
                </li>
                <li className="flex gap-3">
                  <ChevronRight className="text-pink-500 shrink-0 mt-1" size={18} />
                  <div>
                    <span className="font-medium">{t('how_it_works.arrange')}</span> - {t('how_it_works.arrange_desc')}
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-card rounded-xl p-8 shadow-md">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mb-6">
                <Heart className="text-purple-500" size={24} />
              </div>
              <h2 className="text-2xl font-semibold mb-4">{t('how_it_works.for_escorts')}</h2>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <ChevronRight className="text-purple-500 shrink-0 mt-1" size={18} />
                  <div>
                    <span className="font-medium">{t('how_it_works.create_profile')}</span> - {t('how_it_works.create_profile_desc')}
                  </div>
                </li>
                <li className="flex gap-3">
                  <ChevronRight className="text-purple-500 shrink-0 mt-1" size={18} />
                  <div>
                    <span className="font-medium">{t('how_it_works.get_verified')}</span> - {t('how_it_works.get_verified_desc')}
                  </div>
                </li>
                <li className="flex gap-3">
                  <ChevronRight className="text-purple-500 shrink-0 mt-1" size={18} />
                  <div>
                    <span className="font-medium">{t('how_it_works.offer_subscriptions')}</span> - {t('how_it_works.offer_subscriptions_desc')}
                  </div>
                </li>
                <li className="flex gap-3">
                  <ChevronRight className="text-purple-500 shrink-0 mt-1" size={18} />
                  <div>
                    <span className="font-medium">{t('how_it_works.manage_bookings')}</span> - {t('how_it_works.manage_bookings_desc')}
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-card rounded-lg p-6 text-center">
              <Shield className="mx-auto mb-4 text-primary" size={32} />
              <h3 className="text-xl font-semibold mb-2">{t('how_it_works.secure_private')}</h3>
              <p className="text-muted-foreground">{t('how_it_works.secure_private_desc')}</p>
            </div>
            
            <div className="bg-card rounded-lg p-6 text-center">
              <Info className="mx-auto mb-4 text-primary" size={32} />
              <h3 className="text-xl font-semibold mb-2">{t('how_it_works.verified_profiles')}</h3>
              <p className="text-muted-foreground">{t('how_it_works.verified_profiles_desc')}</p>
            </div>
            
            <div className="bg-card rounded-lg p-6 text-center">
              <CreditCard className="mx-auto mb-4 text-primary" size={32} />
              <h3 className="text-xl font-semibold mb-2">{t('how_it_works.secure_payments')}</h3>
              <p className="text-muted-foreground">{t('how_it_works.secure_payments_desc')}</p>
            </div>
          </div>

          <div className="bg-card rounded-xl p-8 border border-pink-100 dark:border-pink-900/20 mb-12">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <MessageCircle className="text-primary" size={24} />
              {t('how_it_works.faq')}
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-lg mb-2">{t('how_it_works.faq_privacy')}</h3>
                <p className="text-muted-foreground">{t('how_it_works.faq_privacy_answer')}</p>
              </div>
              <div>
                <h3 className="font-medium text-lg mb-2">{t('how_it_works.faq_subscription')}</h3>
                <p className="text-muted-foreground">{t('how_it_works.faq_subscription_answer')}</p>
              </div>
              <div>
                <h3 className="font-medium text-lg mb-2">{t('how_it_works.faq_verification')}</h3>
                <p className="text-muted-foreground">{t('how_it_works.faq_verification_answer')}</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4">{t('how_it_works.ready')}</h2>
            <p className="text-muted-foreground mb-6">{t('how_it_works.join_today')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to={`/${currentLanguage}/register`}>{t('how_it_works.create_account')}</Link>
              </Button>
              <Button variant="outline" asChild size="lg">
                <Link to={`/${currentLanguage}/explore`}>{t('how_it_works.explore_profiles')}</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HowItWorks;
